import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Avatar from '@mui/material/Avatar';
import Typography from '@mui/material/Typography';
import ListItemText from '@mui/material/ListItemText';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import ListItemButton from '@mui/material/ListItemButton';
import NextLink from 'next/link';
import { fToNow } from '@/components/common/utils/format-time';
import { Label } from '@/components/common/label';
import { Iconify } from '@/components/common/iconify';
import { alpha, useTheme } from '@mui/material/styles';

const CATEGORY_ICON: Record<string, { icon: string; color: 'primary' | 'success' | 'warning' | 'info' | 'secondary' }> = {
  // Campaigns & Marketing Channels
  'Campaign':        { icon: 'solar:target-bold-duotone',                 color: 'primary' },
  'Campaigns':       { icon: 'solar:target-bold-duotone',                 color: 'primary' },
  'Digital Ads':     { icon: 'solar:monitor-smartphone-bold-duotone',     color: 'info' },
  'Digital Ad':      { icon: 'solar:monitor-smartphone-bold-duotone',     color: 'info' },
  'Direct Mail':     { icon: 'solar:letter-bold-duotone',                 color: 'warning' },
  'EDDM':            { icon: 'solar:map-point-bold-duotone',              color: 'warning' },
  'Email':           { icon: 'solar:inbox-line-bold-duotone',             color: 'info' },
  'Email Campaign':  { icon: 'solar:inbox-line-bold-duotone',             color: 'info' },
  'Audience':        { icon: 'solar:users-group-rounded-bold-duotone',    color: 'primary' },
  'Predictions':     { icon: 'solar:chart-2-bold-duotone',                color: 'secondary' },
  'Voice':           { icon: 'solar:phone-calling-bold-duotone',          color: 'info' },
  'Voice Agent':     { icon: 'solar:smart-speaker-minimalistic-bold-duotone', color: 'info' },
  'Journey Builder': { icon: 'solar:route-bold-duotone',                  color: 'primary' },

  // Finance & Wallet
  'Wallet':          { icon: 'solar:wallet-bold-duotone',                 color: 'success' },
  'Payment':         { icon: 'solar:card-bold-duotone',                   color: 'success' },
  'Billing':         { icon: 'solar:bill-list-bold-duotone',              color: 'success' },
  'Order':           { icon: 'solar:bag-bold-duotone',                    color: 'info' },
  'Orders':          { icon: 'solar:bag-bold-duotone',                    color: 'info' },

  // System & Analytics
  'Analytics':       { icon: 'solar:chart-bold-duotone',                  color: 'info' },
  'Platform':        { icon: 'solar:star-bold-duotone',                   color: 'warning' },
  'Account':         { icon: 'solar:user-bold-duotone',                   color: 'warning' },
  'User':            { icon: 'solar:user-bold-duotone',                   color: 'warning' },
};

// ----------------------------------------------------------------------

export type NotificationItemProps = {
  id: string;
  type: string;
  title: string;
  category: string;
  isUnRead: boolean;
  avatarUrl: string | null;
  createdAt: string | number | null;
  href?: string;
};

export function NotificationItem({
  notification,
  onClick,
}: {
  notification: NotificationItemProps;
  onClick?: () => void;
}) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const meta = CATEGORY_ICON[notification.category] ?? { icon: 'solar:bell-bing-bold-duotone', color: 'primary' };

  const getColors = () => {
    switch (meta.color) {
      case 'primary':
        return {
          icon: isDark ? 'var(--accent-text, #3edcd5)' : (theme.palette.primary.main || '#005457'),
          bg: isDark ? 'var(--accent-bg, rgba(62, 220, 213, 0.16))' : alpha(theme.palette.primary.main || '#005457', 0.12),
        };
      case 'success':
        return {
          icon: isDark ? (theme.palette.success.light || '#69D391') : (theme.palette.success.main || '#005F6E'),
          bg: alpha(theme.palette.success.main, isDark ? 0.22 : 0.12),
        };
      case 'warning':
        return {
          icon: isDark ? (theme.palette.warning.light || '#FFD666') : (theme.palette.warning.main || '#FFAB00'),
          bg: alpha(theme.palette.warning.main, isDark ? 0.22 : 0.12),
        };
      case 'info':
        return {
          icon: isDark ? (theme.palette.info.light || '#4DB6AC') : (theme.palette.info.main || '#009688'),
          bg: alpha(theme.palette.info.main, isDark ? 0.22 : 0.12),
        };
      case 'secondary':
        return {
          icon: isDark ? '#03DAC5' : (theme.palette.secondary.main || '#03DAC5'),
          bg: alpha(theme.palette.secondary.main || '#03DAC5', isDark ? 0.22 : 0.12),
        };
      default:
        return {
          icon: isDark ? 'var(--accent-text, #3edcd5)' : theme.palette.primary.main,
          bg: isDark ? 'var(--accent-bg, rgba(62, 220, 213, 0.16))' : alpha(theme.palette.primary.main, 0.12),
        };
    }
  };

  const { icon: iconColor, bg: iconBg } = getColors();

  const renderAvatar = (
    <ListItemAvatar>
      {notification.avatarUrl ? (
        <Avatar src={notification.avatarUrl} sx={{ bgcolor: 'background.neutral' }} />
      ) : (
        <Stack
          alignItems="center"
          justifyContent="center"
          sx={{
            width: 40,
            height: 40,
            flexShrink: 0,
            borderRadius: '10px',
            bgcolor: iconBg,
            color: iconColor,
          }}
        >
          <Iconify icon={meta.icon} width={22} />
        </Stack>
      )}
    </ListItemAvatar>
  );

  const renderText = (
    <ListItemText
      disableTypography
      primary={reader(notification.title)}
      secondary={
        <Stack
          direction="row"
          alignItems="center"
          sx={{ typography: 'caption', color: 'text.disabled' }}
          divider={
            <Box
              sx={{
                width: 2,
                height: 2,
                bgcolor: 'currentColor',
                mx: 0.5,
                borderRadius: '50%',
              }}
            />
          }
        >
          {fToNow(notification.createdAt)}
          {notification.category}
        </Stack>
      }
    />
  );

  const renderUnReadBadge = notification.isUnRead && (
    <Box
      sx={{
        top: 26,
        width: 8,
        height: 8,
        right: 20,
        borderRadius: '50%',
        bgcolor: 'info.main',
        position: 'absolute',
      }}
    />
  );

  const friendAction = (
    <Stack spacing={1} direction="row" sx={{ mt: 1.5 }}>
      <Button size="small" variant="contained">
        Accept
      </Button>
      <Button size="small" variant="outlined">
        Decline
      </Button>
    </Stack>
  );

  const projectAction = (
    <Stack alignItems="flex-start">
      <Box
        sx={{
          p: 1.5,
          my: 1.5,
          borderRadius: 1.5,
          color: 'text.primary',
          bgcolor: 'background.neutral',
        }}
      >
        {reader(
          `<p><strong>@Jaydon Frankie</strong> feedback by asking questions or just leave a note of appreciation.</p>`
        )}
      </Box>

      <Button size="small" variant="contained">
        Reply
      </Button>
    </Stack>
  );

  const fileAction = (
    <Stack
      spacing={1}
      direction="row"
      sx={{
        pl: 1,
        p: 1.5,
        mt: 1.5,
        borderRadius: 1.5,
        bgcolor: 'background.neutral',
      }}
    >
      {/* <FileThumbnail file="http://localhost:8080/httpsdesign-suriname-2015.mp3" /> */}

      <Stack spacing={1} direction={{ xs: 'column', sm: 'row' }} flexGrow={1} sx={{ minWidth: 0 }}>
        <ListItemText
          disableTypography
          primary={
            <Typography variant="subtitle2" component="div" sx={{ color: 'text.primary' }} noWrap>
              design-suriname-2015.mp3
            </Typography>
          }
          secondary={
            <Stack
              direction="row"
              alignItems="center"
              sx={{ typography: 'caption', color: 'text.disabled' }}
              divider={
                <Box
                  sx={{
                    mx: 0.5,
                    width: 2,
                    height: 2,
                    borderRadius: '50%',
                    bgcolor: 'currentColor',
                  }}
                />
              }
            >
              <span>2.3 GB</span>
              <span>30 min ago</span>
            </Stack>
          }
        />

        <Button size="small" variant="outlined">
          Download
        </Button>
      </Stack>
    </Stack>
  );

  const tagsAction = (
    <Stack direction="row" spacing={0.75} flexWrap="wrap" sx={{ mt: 1.5 }}>
      <Label variant="outlined" color="info">
        Design
      </Label>
      <Label variant="outlined" color="warning">
        Dashboard
      </Label>
      <Label variant="outlined">Design system</Label>
    </Stack>
  );

  const paymentAction = (
    <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
      <Button size="small" variant="contained">
        Pay
      </Button>
      <Button size="small" variant="outlined">
        Decline
      </Button>
    </Stack>
  );

  return (
    <ListItemButton
      disableRipple
      onClick={onClick}
      {...(notification.href ? { component: NextLink, href: notification.href } : {})}
      sx={{
        p: 2.5,
        alignItems: 'flex-start',
        borderBottom: (theme) => `dashed 1px ${theme.vars.palette.divider}`,
        ...(notification.href && { cursor: 'pointer' }),
      }}
    >
      {renderUnReadBadge}

      {renderAvatar}

      <Stack sx={{ flexGrow: 1 }}>
        {renderText}
        {notification.type === 'friend' && friendAction}
        {notification.type === 'project' && projectAction}
        {notification.type === 'file' && fileAction}
        {notification.type === 'tags' && tagsAction}
        {notification.type === 'payment' && paymentAction}
      </Stack>
    </ListItemButton>
  );
}

// ----------------------------------------------------------------------

function reader(data: string) {
  return (
    <Box
      dangerouslySetInnerHTML={{ __html: data }}
      sx={{
        mb: 0.5,
        '& p': { typography: 'body2', m: 0 },
        '& a': { color: 'inherit', textDecoration: 'none' },
        '& strong': { typography: 'subtitle2' },
      }}
    />
  );
}
